import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Edit2, Trash2, MapPin, Search, Plane, RefreshCw, X } from 'lucide-react';
import api from '../../services/api';

const RoutePresets = () => {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    pickup: { address: 'Tirana International Airport Nënë Tereza', lat: 41.4186524, lng: 19.7134978 },
    dropoff: { address: '', lat: 41.3275, lng: 19.8187 },
    price_eur: 0,
    price_lek: 0,
    active: true
  });
  const [destinationInput, setDestinationInput] = useState('');

  // Tirana Airport fixed coordinates
  const TIRANA_AIRPORT = {
    address: 'Tirana International Airport Nënë Tereza',
    lat: 41.4186524,
    lng: 19.7134978
  };

  // Initialize Google Places services
  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(document.createElement('div'));
    }
  }, [showModal]);

  // Reset destination input when modal opens
  useEffect(() => {
    if (showModal && !editingPreset) {
      setDestinationInput('');
    } else if (showModal && editingPreset) {
      setDestinationInput(editingPreset.dropoff?.address || '');
    }
  }, [showModal, editingPreset]);

  // Search for places
  const searchPlaces = (input) => {
    setDestinationInput(input);
    
    if (!input || input.length < 2 || !autocompleteService.current) {
      setSuggestions([]);
      return;
    }

    autocompleteService.current.getPlacePredictions(
      {
        input,
        types: ['(cities)'],
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
        }
      }
    );
  };

  // Select a place from suggestions
  const selectPlace = (placeId, description) => {
    if (!placesService.current) return;

    placesService.current.getDetails(
      { placeId, fields: ['geometry', 'name', 'formatted_address'] },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const destName = place.name || description.split(',')[0];
          const fullAddress = place.formatted_address || description;
          
          // Update the input field with the selected place
          setDestinationInput(fullAddress);
          
          // Use functional update to avoid stale closure
          setFormData(prev => ({
            ...prev,
            name: `Aeroport → ${destName}`,
            dropoff: {
              address: fullAddress,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            }
          }));
          setShowSuggestions(false);
          setSuggestions([]);
        }
      }
    );
  };

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async () => {
    try {
      setLoading(true);
      const response = await api.get('/routes/presets?active_only=false&limit=200');
      setPresets(response.data);
    } catch (error) {
      console.error('Error fetching presets:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter presets based on search
  const filteredPresets = useMemo(() => {
    if (!searchTerm.trim()) return presets;
    const term = searchTerm.toLowerCase();
    return presets.filter(preset => 
      preset.name?.toLowerCase().includes(term) ||
      preset.dropoff?.address?.toLowerCase().includes(term) ||
      preset.pickup?.address?.toLowerCase().includes(term)
    );
  }, [presets, searchTerm]);

  // Calculate stats
  const stats = useMemo(() => {
    const domestic = presets.filter(p => !p.name?.includes('Kosovo') && !p.name?.includes('Montenegro') && !p.name?.includes('Macedonia'));
    const international = presets.filter(p => p.name?.includes('Kosovo') || p.name?.includes('Montenegro') || p.name?.includes('Macedonia'));
    const avgPrice = presets.length > 0 
      ? Math.round(presets.reduce((sum, p) => sum + (p.price_eur || 0), 0) / presets.length)
      : 0;
    return { total: presets.length, domestic: domestic.length, international: international.length, avgPrice };
  }, [presets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        name: formData.name,
        description: formData.description || '',
        pickup: {
          address: formData.pickup.address || TIRANA_AIRPORT.address,
          position: {
            lat: formData.pickup.lat || TIRANA_AIRPORT.lat,
            lng: formData.pickup.lng || TIRANA_AIRPORT.lng
          }
        },
        dropoff: {
          address: formData.dropoff.address || 'Unknown',
          position: {
            lat: formData.dropoff.lat || 41.3275,
            lng: formData.dropoff.lng || 19.8187
          }
        },
        price_eur: parseFloat(formData.price_eur) || 0,
        price_lek: parseFloat(formData.price_lek) || 0,
        fixed_price: parseFloat(formData.price_lek) || 0,
        distance: 0,
        estimated_duration: 0,
        active: formData.active
      };
      
      console.log('Saving preset:', payload);

      if (editingPreset) {
        await api.put(`/routes/presets/${editingPreset.id}`, payload);
      } else {
        await api.post('/routes/presets', payload);
      }
      
      // Success - close modal and refresh
      closeModal();
      await fetchPresets();
      
    } catch (error) {
      console.error('Error saving preset:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.status === 401) {
        alert('Sesioni ka skaduar. Ju lutem identifikohuni përsëri.');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        alert('Gabim gjatë ruajtjes: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleDelete = async (presetId) => {
    if (!window.confirm('Jeni të sigurt që dëshironi të fshini këtë rrugë?')) return;
    try {
      await api.delete(`/routes/presets/${presetId}`);
      await fetchPresets();
    } catch (error) {
      console.error('Error deleting preset:', error);
      alert('Gabim gjatë fshirjes');
    }
  };

  const openAddModal = () => {
    setEditingPreset(null);
    setFormData({
      name: '',
      description: '',
      pickup: { address: 'Tirana International Airport Nënë Tereza', lat: 41.4186524, lng: 19.7134978 },
      dropoff: { address: '', lat: 41.3275, lng: 19.8187 },
      price_eur: 0,
      price_lek: 0,
      active: true
    });
    setShowModal(true);
  };

  const openEditModal = (preset) => {
    setEditingPreset(preset);
    setFormData({
      name: preset.name,
      description: preset.description || '',
      pickup: {
        address: preset.pickup?.address || 'Tirana International Airport Nënë Tereza',
        lat: preset.pickup?.lat || 41.4186524,
        lng: preset.pickup?.lng || 19.7134978
      },
      dropoff: {
        address: preset.dropoff?.address || '',
        lat: preset.dropoff?.lat || 41.3275,
        lng: preset.dropoff?.lng || 19.8187
      },
      price_eur: preset.price_eur || 0,
      price_lek: preset.price_lek || preset.price || preset.fixed_price || 0,
      active: preset.active !== false && preset.is_active !== false
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPreset(null);
  };

  const getPriceEur = (preset) => {
    if (preset.price_eur != null && preset.price_eur !== '') return Number(preset.price_eur);
    const lek = preset.price_lek || preset.price || preset.fixed_price || 0;
    return lek ? Math.round(lek / 100) : 0;
  };

  const getPriceLek = (preset) => {
    if (preset.price_lek) return Number(preset.price_lek);
    if (preset.price) return Number(preset.price);
    if (preset.fixed_price) return Number(preset.fixed_price);
    if (preset.price_eur) return Math.round(Number(preset.price_eur) * 100);
    return 0;
  };

  // Get destination name from preset name (remove "Aeroport → ")
  const getDestination = (preset) => {
    return preset.name?.replace('Aeroport → ', '').replace('Aeroport - ', '') || preset.dropoff?.address || 'N/A';
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <Plane className="w-8 h-8 text-red-600" />
              Lista e Çmimeve - Aeroporti Tirana
            </h1>
            <p className="text-gray-600">Çmimet fikse nga Aeroporti Ndërkombëtar "Nënë Tereza"</p>
          </div>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg font-semibold transition shadow-md"
          >
            <Plus size={18} />
            Shto Destinacion
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-500">Total Destinacione</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="text-3xl font-bold text-blue-600">{stats.domestic}</div>
            <div className="text-sm text-gray-500">Brenda Shqipërisë</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="text-3xl font-bold text-purple-600">{stats.international}</div>
            <div className="text-sm text-gray-500">Ndërkombëtare</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="text-3xl font-bold text-green-600">€{stats.avgPrice}</div>
            <div className="text-sm text-gray-500">Çmimi Mesatar (EUR)</div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Kërko destinacion... (p.sh. Durrës, Vlorë, Prishtinë)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm"
          />
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider w-12">#</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Destinacioni</th>
                <th className="text-center px-2 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider w-24">EUR (€)</th>
                <th className="text-center px-2 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider w-28">LEK (L)</th>
                <th className="text-center px-2 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider w-16">Statusi</th>
                <th className="text-center px-2 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider w-20">Veprime</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-gray-300" />
                    Duke ngarkuar...
                  </td>
                </tr>
              ) : filteredPresets.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-gray-500">
                    {searchTerm ? 'Nuk u gjet asnjë destinacion me këtë kërkim' : 'Nuk ka destinacione'}
                  </td>
                </tr>
              ) : (
                filteredPresets.map((preset, index) => (
                  <tr 
                    key={preset.id} 
                    className="hover:bg-gray-50 transition-colors"
                  >
                    {/* Index */}
                    <td className="px-4 py-2.5">
                      <span className="font-mono text-sm text-gray-400">
                        {index + 1}
                      </span>
                    </td>

                    {/* Destination */}
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <span className="font-medium text-gray-900 text-sm">{getDestination(preset)}</span>
                      </div>
                    </td>

                    {/* EUR Price */}
                    <td className="px-2 py-2.5 text-center">
                      <span className="text-blue-600 font-semibold text-sm">
                        €{getPriceEur(preset).toLocaleString('en-US')}
                      </span>
                    </td>

                    {/* LEK Price */}
                    <td className="px-2 py-2.5 text-center">
                      <span className="text-green-700 font-semibold text-sm">
                        {getPriceLek(preset).toLocaleString('en-US')} L
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-2 py-2.5 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        (preset.active !== false && preset.is_active !== false)
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {(preset.active !== false && preset.is_active !== false) ? 'Aktiv' : 'Jo'}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-2 py-2.5">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openEditModal(preset)}
                          className="p-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded transition"
                          title="Ndrysho"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(preset.id)}
                          className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded transition"
                          title="Fshi"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        {!loading && filteredPresets.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
            <span className="text-sm text-gray-500">
              Duke shfaqur {filteredPresets.length} nga {presets.length} destinacione
            </span>
            <button
              onClick={fetchPresets}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <RefreshCw size={14} />
              Rifresko
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-5 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingPreset ? 'Ndrysho Destinacionin' : 'Shto Destinacion të Ri'}
              </h2>
              <button 
                onClick={closeModal} 
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Pickup - Fixed Tirana Airport */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <Plane className="inline w-4 h-4 mr-1 text-red-500" />
                  Nisja (Aeroporti)
                </label>
                <input
                  type="text"
                  value={TIRANA_AIRPORT.address}
                  disabled
                  className="w-full px-4 py-2.5 bg-gray-100 border border-gray-300 rounded-lg text-gray-600 cursor-not-allowed"
                />
              </div>

              {/* Destination with Google Places Autocomplete */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <MapPin className="inline w-4 h-4 mr-1 text-green-500" />
                  Destinacioni
                </label>
                <input
                  type="text"
                  value={destinationInput}
                  placeholder="Kërko qytet... (p.sh. Budva, Prishtinë, Durrës)"
                  onChange={(e) => searchPlaces(e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition"
                />
                
                {/* Suggestions Dropdown */}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {suggestions.map((suggestion) => (
                      <button
                        key={suggestion.place_id}
                        type="button"
                        onClick={() => selectPlace(suggestion.place_id, suggestion.description)}
                        className="w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100 last:border-0"
                      >
                        <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{suggestion.description}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Route Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Emri i Rrugës
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
                  required
                  placeholder="p.sh. Aeroport → Budva"
                />
              </div>

              {/* Prices */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Çmimi EUR (€)
                  </label>
                  <input
                    type="number"
                    value={formData.price_eur}
                    onChange={(e) => {
                      const eur = parseFloat(e.target.value) || 0;
                      setFormData({ 
                        ...formData, 
                        price_eur: eur,
                        price_lek: Math.round(eur * 100)
                      });
                    }}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    required
                    min="0"
                    step="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Çmimi LEK (L)
                  </label>
                  <input
                    type="number"
                    value={formData.price_lek}
                    onChange={(e) => setFormData({ ...formData, price_lek: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none transition"
                    required
                    min="0"
                  />
                </div>
              </div>

              {/* Active Toggle */}
              <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <span className="text-sm font-medium text-gray-700">Rruga aktive</span>
              </label>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    console.log('Submit button clicked');
                    console.log('Form data:', formData);
                    // Trigger form submit
                    const form = document.querySelector('form');
                    if (form) form.requestSubmit();
                  }}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-lg transition"
                >
                  {editingPreset ? 'Ruaj Ndryshimet' : 'Shto'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 rounded-lg transition"
                >
                  Anulo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutePresets;
